import {Injectable} from "@angular/core";
import {Action, Store} from "@ngrx/store";
import {Actions, createEffect, ofType} from "@ngrx/effects";

import {SharedService} from "./shared.service";
import * as fromRoot from "../../../state/app.state";
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {ProfileModel} from "../../../models/profile.model";
import {CommonService} from "../services/common.service";
import {FetchProfileModel} from "../models/fetch-profile.model";
import * as SharedActions from "../../shared/core/shared.actions";
import {
  DeleteUserFailure,
  DeleteUserRequest,
  DeleteUserSuccess,
  FetchAvatarFilterFailure,
  FetchAvatarFilterRequest,
  FetchAvatarFilterSuccess,
  FetchGenderFilterFailure,
  FetchGenderFilterRequest,
  FetchGenderFilterSuccess,
  FetchNotificationDetailsFailure,
  FetchNotificationDetailsRequest,
  FetchNotificationDetailsSuccess,
  FetchUserStatusFilterFailure,
  FetchUserStatusFilterRequest,
  FetchUserStatusFilterSuccess,
  GenerateOtpFailure,
  GenerateOtpRequest,
  GenerateOtpSuccess,
  OtpSentStatus,
  SaveAnswersFailure,
  SaveAnswersRequest,
  SaveAnswersSuccess,
  VerifyOtpFailure,
  VerifyOtpRequest,
  VerifyOtpSuccess
} from "./shared.actions";
import {UpdatePasswordModel} from "../models/update-password.model";
import {GenerateUsername} from "../models/generate-username";
import {VerifyUsername} from "../models/verify-username";
import {EditProfileModel} from "../models/edit-profile.model";
import {ActivatedRoute, Router} from "@angular/router";
import {
  SaveAnswersModel
} from "../../page-modules/participants-list/models/save-answers.model";
import {GenerateOtpModel} from "../../authentication/models/generate-otp.model";
import {VerifyOtpModel} from "../../authentication/models/verify-otp.model";
import {NotificationsService} from "../services/notifications.service";
import * as moment from 'moment'
import {MessageService} from "primeng/api";
import {
  FetchNotificationListRequest
} from "../../page-modules/notifications/core/notifications.actions";
import {
  FetchGroupFilterModel
} from "../../page-modules/clients-list/model/fetch-group-filter.model";
import {
  FetchUserStatusFilterModel
} from "../models/fetch-user-status-filter.model";
import {
  DeleteClientModel
} from "../../page-modules/client-details/models/delete-client.model";
import {ManageUsersService} from '../../page-modules/manage-users/core/manage-users.service';

@Injectable()
export class SharedEffects {
  fetchProfile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.FetchProfileRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap(() =>
        this.sharedService.fetchProfile().pipe(
          map((response: FetchProfileModel["response"]) => {
            const {data, message} = response;
            const profile: ProfileModel = {
              ...data,
              roleName: data.role.value,
              role: data.role.key,
            };
            return SharedActions.FetchProfileSuccess({profile, message});
          }),
          catchError(() => {
            return of(SharedActions.FetchProfileFailure());
          }),
          tap((action: any) => {
            if (action.type === SharedActions.FetchProfileSuccess.type) {
              // Code to execute on API Success Action dispatc
              this.router.navigate([]);
            } else if (action.type === SharedActions.FetchProfileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchProfileByUserId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.FetchProfileByUserIdRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchProfileModel["request"]) =>
        this.sharedService.fetchProfile(payload).pipe(
          map((response: FetchProfileModel["response"]) => {
            const {data, message} = response;
            const profile: ProfileModel = {
              ...data,
              roleName: data.role.value,
              role: data.role.key,
            };
            return SharedActions.FetchProfileByUserIdSuccess({
              profile,
              message
            });
          }),
          catchError(() => {
            return of(SharedActions.FetchProfileByUserIdFailure());
          }),
          tap((action: any) => {
            if (action.type === SharedActions.FetchProfileByUserIdSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === SharedActions.FetchProfileByUserIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  updatePassword$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.UpdatePasswordRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload: UpdatePasswordModel["request"]) =>
        this.sharedService.updatePassword(payload).pipe(
          map((response: UpdatePasswordModel["response"]) => {
            return SharedActions.UpdatePasswordSuccess({message: response.message});
          }),
          catchError((error) => {
            return of(SharedActions.UpdatePasswordFailure(error));
          }),
          tap((action) => {
            if (action.type === SharedActions.UpdatePasswordSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.notification(action.message, "success");
              this.commonService.closeDialog();
            } else if (action.type === SharedActions.UpdatePasswordFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  autoGenerateUsername$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.GenerateUsernameRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sharedService.generateUsername(payload).pipe(
          map((response: GenerateUsername["response"]) => {
            const {data} = response;

            return SharedActions.GenerateUsernameSuccess({
              username: data.username,
              uuid: data.uuid
            });
          }),
          catchError(() => {
            return of(SharedActions.GenerateUsernameFailure());
          }),
          tap((action) => {
            if (action.type === SharedActions.GenerateUsernameSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === SharedActions.GenerateUsernameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  verifyUsername$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.VerifyUsernameRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sharedService.verifyUsername(payload).pipe(
          map((response: VerifyUsername["response"]) => {
            const {data, message} = response;

            return SharedActions.VerifyUsernameSuccess({
              isAvailable: data.isAvailable,
              uuid: data.uuid,
              message
            });
          }),
          catchError(() => {
            return of(SharedActions.VerifyUsernameFailure());
          }),
          tap((action: any) => {
            if (action.type === SharedActions.VerifyUsernameSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === SharedActions.VerifyUsernameFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );
  saveAnswers$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SaveAnswersRequest),
      map((action: any) => {
        this.commonService.startLoading();
        this.commonService.urlParams.subscribe(data => {
        }).unsubscribe()
        return action.payload;
      }),
      mergeMap((payload: SaveAnswersModel["request"]) =>
        this.sharedService.saveAnswers(payload).pipe(
          map((response: SaveAnswersModel["response"]) => {
            const {data, message} = response;
            const profile: ProfileModel = {
              ...data?.profile,
              roleName: data?.profile?.role?.value,
              role: data?.profile?.role?.key,
            };

            let userId;

            this.commonService.urlParams.subscribe(data => {
              const {params} = data;

              userId = params.clientAdminId || params.coachId || params.memberId;
            }).unsubscribe()

            if (userId) {
              return SaveAnswersSuccess({
                selectedUserProfileData: {
                  loading: false,
                  profile
                },
                saveAnswerInfo: {
                  loading: false
                }
              })
            }

            return SaveAnswersSuccess({
              profileData: {
                loading: false,
                profile
              },
              saveAnswerInfo: {
                loading: false
              }
            });
          }),
          catchError(() => {
            return of(SaveAnswersFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === SaveAnswersSuccess.type) {
              // Code to execute on API Success Action dispatch
              // this.store.dispatch(FetchProfileRequest());
              // this.commonService.notification(action.message, "success");
            } else if (action.type === SaveAnswersFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  generateOtpRequest$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(GenerateOtpRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload: GenerateOtpModel["request"]) =>
        this.sharedService.generateOtp(payload).pipe(
          map((response: GenerateOtpModel["response"]) => {
            const {message} = response;
            return GenerateOtpSuccess({message});
          }),
          catchError((error) => {
            return of(GenerateOtpFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === GenerateOtpSuccess.type) {
              // Code to execute on API Success Action dispatch
            } else if (action.type === GenerateOtpFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  verifyOtp$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(VerifyOtpRequest),
      map((action) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sharedService.verifyOtp(payload).pipe(
          map((response: VerifyOtpModel["response"]) => {
            const {message} = response;
            return VerifyOtpSuccess({
              message
            });
          }),
          catchError((error) => {
            return of(VerifyOtpFailure({message: error.message}));
          }),
          tap((action) => {
            if (action.type === VerifyOtpSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              this.commonService.notification(
                "Phone Number Updated Successfully",
                "success"
              );
              this.store.dispatch(OtpSentStatus({otpSent: false}));
            } else if (action.type === VerifyOtpFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchGenderFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchGenderFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.sharedService.genderFilter().pipe(
          map((response: FetchGroupFilterModel["response"]) => {
            const {data, message} = response;
            return FetchGenderFilterSuccess({
              genderList: data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchGenderFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchGenderFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchGenderFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    ));

  fetchNotificationDetails$: Observable<Action> = createEffect(() => this.actions.pipe(ofType(FetchNotificationDetailsRequest), map(actions => actions.payload), mergeMap(payload => this.notificationService.fetchNotificationDetails({notificationId: payload.notificationId})
    .pipe(map(res => FetchNotificationDetailsSuccess({payload: {notificationDetails: res.data}})), catchError(err => of(FetchNotificationDetailsFailure())), tap((action: any) => {
      if (action.type === FetchNotificationDetailsSuccess.type) {
        // Code to execute on API Success Action dispatch
      } else if (action.type === FetchNotificationDetailsFailure.type) {
        // Code to execute on API Failure Action dispatch
      }
    })))))

  confirmParticipantInfo: boolean = false;
  userId: string = "";
  editProfile$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.EditProfileRequest),
      map((action: any) => {
        this.commonService.startLoading();
        this.confirmParticipantInfo = action.confirmParticipantInfo;
        this.userId = action.payload.forUserId;
        return action.payload;
      }),
      mergeMap((payload: EditProfileModel["request"]) =>
        this.sharedService.editProfile(payload).pipe(
          map((response: EditProfileModel["response"]) => {
            const {data, message} = response;
            this.commonService.removeOnboardingToken();
            if (data.token) {
              const token = data.token;
              this.commonService.setAuthenticationToken(token);
            }

            const profile = {
              ...data.profile,
              roleName: data.profile.role.value,
              role: data.profile.role.key,
            };

            return SharedActions.EditProfileSuccess({profile, message});
          }),
          catchError(() => {
            return of(SharedActions.EditProfileFailure());
          }),
          tap((action: any) => {
            this.commonService.stopLoading();
            if (action.type === SharedActions.EditProfileSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (this.confirmParticipantInfo) {
                if (payload["isLar"]) {
                  this.router.navigate([`adult/my-participants/relation/${this.userId}`], {queryParams: { isLar: payload["isLar"] }});
                  return;
                } else {
                  this.router.navigate((["adult/my-participants"]));
                  return;
                }
              }
              this.commonService.notification(action.message, "success");

            } else if (action.type === SharedActions.EditProfileFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  editProfileByUserId$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(SharedActions.EditProfileByUserIdRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sharedService.editProfile(payload).pipe(
          map((response: EditProfileModel["response"]) => {
            const {data, message} = response;
            const profile: ProfileModel = {
              ...data.profile,
              roleName: data.profile.role.value,
              role: data.profile.role.key,
            };
            return SharedActions.EditProfileByUserIdSuccess({
              profile,
              message
            });
          }),
          catchError(() => {
            return of(SharedActions.EditProfileByUserIdFailure());
          }),
          tap((action: any) => {
            if (action.type === SharedActions.EditProfileByUserIdSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              this.commonService.notification(action.message, "success");
            } else if (action.type === SharedActions.EditProfileByUserIdFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  markNotificationAsRead$ = createEffect(() => this.actions.pipe(ofType(SharedActions.MarkNotificationAsReadRequest),
      map(action => action.payload), mergeMap(payload => {
        this.commonService.startLoading();
        return this.notificationService.markNotificationAsRead({notificationId: [...payload.notificationId]})
          .pipe(map(response => {
              return SharedActions.MarkNotificationAsReadSuccess()
            }
          ), catchError(err => of(SharedActions.MarkNotificationAsReadFailure())))
      }),
      tap((action: any) => {
        if (action.type === SharedActions.MarkNotificationAsReadSuccess.type) {
          // Code to execute on API Success Action dispatch
          this.store.dispatch(FetchNotificationListRequest({
              payload: {
                pageNumber: 1
              }
            }
          ))
        } else if (action.type === SharedActions.MarkNotificationAsReadFailure.type) {
          // Code to execute on API Failure Action dispatch
        }
        this.commonService.stopLoading();
      })
    )
  )

  fetchLatestNotification$ = createEffect(() => this.actions.pipe(ofType(SharedActions.FetchLatestNotificationForPopupRequest),
    map(action => action.payload),
    mergeMap(payload => {
      const params: any = {
        ...payload,
        limit: -1,
        read: false
      }
      return this.notificationService.fetchNotificationList({...params})
        .pipe(map((response) => {
            this.messageService.clear();
            response.data.list.map((notification, index: number) => {
              if (index < 5) {
                this.notificationService.showNotification({...notification})
              }
            })
            return SharedActions.FetchLatestNotificationForPopupSuccess({payload: {currentTimestamp: moment()}})
          }),
          catchError(err => of(SharedActions.FetchLatestNotificationForPopupFailure())))
    }),
    tap((action: any) => {
      if (action.type === SharedActions.FetchLatestNotificationForPopupSuccess.type) {
        // Code to execute on API Success Action dispatch
      } else if (action.type === SharedActions.FetchLatestNotificationForPopupFailure.type) {
        // Code to execute on API Failure Action dispatch
      }
    })
  ))

  triggerNotification$ = createEffect(() => this.actions.pipe(ofType(SharedActions.TriggerNotificationRequest), map(actions => actions.payload), mergeMap(payload => {
    this.commonService.startLoading();
    return this.notificationService.triggerNotification(payload)
      .pipe(map((response: any) => SharedActions.TriggerNotificationSuccess({payload: {message: response.message}})), catchError(err => of(SharedActions.TriggerNotificationFailure())))
  }), tap((action: any) => {
    if (action.type === SharedActions.TriggerNotificationSuccess.type) {
      // Code to execute on API Success Action dispatch
      this.commonService.notification(action.payload.message, 'success')
    } else if (action.type === SharedActions.TriggerNotificationFailure.type) {
      // Code to execute on API Failure Action dispatch
    }
    this.commonService.stopLoading();
  })))

  deleteSelectedNotifications$ = createEffect(() => this.actions.pipe(ofType(SharedActions.DeleteNotificationRequest),
      map(action => action.payload), mergeMap(payload => {
        this.commonService.startLoading();
        return this.notificationService.deleteNotificationAsRead({notificationId: [...payload.notificationId]})
          .pipe(map(response => {
              return SharedActions.DeleteNotificationSuccess({payload: {message: response.message}})
            }
          ), catchError(err => of(SharedActions.DeleteNotificationFailure())))
      }),
      tap((action: any) => {
        if (action.type === SharedActions.DeleteNotificationSuccess.type) {
          // Code to execute on API Success Action dispatch
          this.commonService.notification(action.payload.message, 'success')
          this.store.dispatch(FetchNotificationListRequest({
              payload: {
                pageNumber: 1
              }
            }
          ))
        } else if (action.type === SharedActions.DeleteNotificationFailure.type) {
          // Code to execute on API Failure Action dispatch
        }
        this.commonService.stopLoading();
      })
    )
  )

  fetchUserStatusFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchUserStatusFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.sharedService.fetchUserStatusFilter().pipe(
          map((response: FetchUserStatusFilterModel["response"]) => {
            const {data, message} = response;
            return FetchUserStatusFilterSuccess({
              userStatusList: data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchUserStatusFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchUserStatusFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchUserStatusFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    ));

  fetchAvatarFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchAvatarFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.sharedService.avatarFilter().pipe(
          map((response) => {
            let {data, message} = response;
            data = data.map(e => ({
              ...e,
              isSelected: false
            }));

            return FetchAvatarFilterSuccess({
              avatarOptions: data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchAvatarFilterFailure());
          }),
          tap((action: any) => {
            if (action.type === FetchAvatarFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchAvatarFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    ));


  deleteUser$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(DeleteUserRequest),
      map((action: any) => {
        this.commonService.startLoading();
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sharedService.deleteUser(payload).pipe(
          map((response: DeleteClientModel['response']) => {
            const {message} = response;
            return DeleteUserSuccess({
              message
            });
          }),
          catchError(() => {
            return of(DeleteUserFailure());
          }),
          tap((action) => {
            if (action.type === DeleteUserSuccess.type) {
              // Code to execute on API Success Action dispatch
              this.commonService.closeDialog();
              const urlPrefix = this.router.url.split('/')[1];
              this.router.navigate([`/${urlPrefix}/manage-users`]);
              this.commonService.notification(
                action.message,
                'success'
              );

              this.manageUserService.fetchUsersListTrigger.next(null);
            } else if (action.type === DeleteUserFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
            this.commonService.stopLoading();
          })
        )
      )
    )
  );

  constructor(
    private commonService: CommonService,
    private sharedService: SharedService,
    private actions: Actions,
    private router: Router,
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private notificationService: NotificationsService,
    private messageService: MessageService,
    private manageUserService: ManageUsersService
  ) {
  }
}
